import type { LoadWarning } from "../services/loadDemand";
import { NOT_ESTABLISHED_WARNING_CODES } from "../services/loadDemandContract";
import { StatusIcon } from "./StatusIcon";
import type { StatusTone } from "./statusTone";

type WarningCode = LoadWarning["code"];

interface LoadWarningPanelProps {
  warnings: LoadWarning[];
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive map: a new code without a label is a compile error. A load warning
// carries only a code and a message - there is no severity and no reference
// field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  ZERO_DEMAND: "Zero demand",
  UTILIZATION_FACTOR_NOT_ESTABLISHED: "Utilization factor not established",
  DEMAND_FACTOR_NOT_ESTABLISHED: "Demand factor not established",
  EFFICIENCY_NOT_ESTABLISHED: "Efficiency not established",
  COINCIDENCE_FACTOR_NOT_ESTABLISHED: "Coincidence factor not established",
};

// The tone of each warning (A25 (d)): a factor not established is a review the engineer
// owes (the result is REVIEW_REQUIRED); a load schedule never fails, so the rest are
// plain warnings.
function toneOf(code: WarningCode): StatusTone {
  return (NOT_ESTABLISHED_WARNING_CODES as readonly string[]).includes(code) ? "review" : "warning";
}

export function LoadWarningPanel({ warnings }: LoadWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <ul data-warning-count={warnings.length}>
          {warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              data-warning-code={warning.code}
              data-warning-tone={toneOf(warning.code)}
            >
              <strong>
                <StatusIcon tone={toneOf(warning.code)} size={16} />
                {WARNING_CODE_LABELS[warning.code]}
              </strong>
              {": "}
              {/* The engine's own sentence; a warning that came from one load of
                  the schedule still names that load with its code prefix. */}
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export { WARNING_CODE_LABELS, toneOf };

export default LoadWarningPanel;
